<template>
    <v-container grid-list-xl justify-space-between>
        <span class="title primary--text">{{ $t('myMedicalInformation') }}</span>
        <v-stepper v-model="step" class="mt-4" style="min-height: 500px;">
            <v-stepper-header class="h-100">
                <template v-for="stepIndex in finalStep -1">
                    <v-stepper-step :step="stepIndex" @click="step >= stepIndex ? step = stepIndex : null"></v-stepper-step>
                    <v-divider v-if="stepIndex < finalStep -1" ></v-divider>
                </template>
            </v-stepper-header>

            <v-stepper-items>
                <v-stepper-content step="1">
                    <v-form ref="patientForm" v-model="patientInfoValid" lazy-validation>
                        <v-layout>
                            <v-flex md10 offset-md1 wrap>
                                <h3 class="headline mb-4">{{ $t('patientInformation') }}</h3>
                                <v-layout row wrap>
                                    <v-flex xs12 sm6>
                                        <v-list two-line>
                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('sex')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{$t(selectedPatient.gender)}}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>

                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('fullName')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{selectedPatient.firstname}} {{selectedPatient.lastname}}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>

                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('birthDate')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{selectedPatient.birth_date | shortDate }}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>
                                        </v-list>
                                    </v-flex>
                                    <v-flex xs12 sm6>
                                        <v-list two-line>
                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('opaleNumber')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{selectedPatient.opale_number}}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>

                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('email')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{selectedPatient.email}}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>
                                            <v-list-tile>
                                                <v-list-tile-content>
                                                    <v-list-tile-sub-title>{{$t('phone')}}</v-list-tile-sub-title>
                                                    <v-list-tile-title>{{selectedPatient.phone}}</v-list-tile-title>
                                                </v-list-tile-content>
                                            </v-list-tile>
                                        </v-list>
                                    </v-flex>
                                </v-layout>
                                <v-alert
                                    :value="true"
                                    color="info"
                                    icon="info"
                                    outline
                                    class="my-4"
                                >
                                    <b class="text--primary">{{$t('patientInformationMessage')}}<a href="tel:0041227196651">+41 (0)22 719 66 51</a>.</b>
                                </v-alert>
                                <v-text-field
                                    v-model="address"
                                    :label="$t('address')"
                                    :rules="addressRules"
                                ></v-text-field>
                                <v-text-field
                                    v-model="postcode"
                                    :label="$t('postcode')"
                                    :rules="postcodeRules"
                                ></v-text-field>
                                <v-text-field
                                    v-model="city"
                                    :label="$t('city')"
                                    :rules="cityRules"
                                ></v-text-field>
                                <v-select
                                    :items="countryList"
                                    v-model="country"
                                    :label="$t('country')"
                                    item-text="name"
                                    item-value="name"
                                    :rules="countryRules"
                                ></v-select>
                                <v-checkbox v-show="false"
                                    v-model="acceptCGU"
                                    :rules="cguRules">
                                    <template v-slot:label>
                                        <div>
                                            {{ $t('iAccept')}}
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on }">
                                                    <a
                                                        @click.stop
                                                        v-on="on"
                                                    >
                                                        {{ $t('termsAndConditions') }}
                                                    </a>
                                                </template>
                                                {{ $t('openNewTab')}}
                                            </v-tooltip>
                                        </div>
                                    </template>
                                </v-checkbox>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="2">
                    <v-form ref="studyForm" lazy-validation>
                        <v-layout>
                            <v-flex md10 offset-md1 wrap>
                                <h3 class="headline mb-4">{{ $t('studies') }}</h3>
                                <div class="text--primary border-red p-3">
                                    <p>
                                        {{ $t('participationEssential') }}
                                    </p>
                                    <p>
                                        {{ $t('pleaseNote') }}
                                    </p>
                                    <ul>
                                        <li>
                                            <b>{{ $t('dataAnonymization') }}</b> {{ $t('dataAnonymizationText') }}
                                        </li>
                                        <li>
                                            <b>{{ $t('impactOnTreatment') }}</b> {{ $t('impactOnTreatmentText') }}
                                        </li>
                                        <li>
                                            <b>{{ $t('freedomOfChoice') }}</b> {{ $t('freedomOfChoiceText') }}
                                        </li>
                                    </ul>
                                    <p class="mt-2">
                                        {{ $t('contributionAppreciated') }}
                                    </p>
                                </div>
                                <v-checkbox
                                    v-model="declineDataCollection"
                                    class="mt-0"
                                    :label="$t('declineDataCollectionLabel')"
                                ></v-checkbox>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="3">
                    <v-form ref="doctorForm" lazy-validation v-model="doctorsInfoValid">
                        <v-layout>
                            <v-flex md10 offset-md1>
                                <div>
                                    <h3 class="headline mb-4">{{ $t('doctors') }}</h3>
                                    <v-autocomplete
                                        v-if="!noDoctor && !isNewDoctor"
                                        v-model="mainDoctorId"
                                        :search-input.sync="newDoctorName"
                                        :items="doctorsList"
                                        :label="$t('mainDoctor')"
                                        item-text="name"
                                        item-value="id"
                                        :rules="doctorRules"
                                    >
                                        <template v-slot:no-data>
                                            <v-list-tile @click="handleNewDoctor">
                                                <v-list-tile-title>
                                                    {{$t('add')}} <strong>{{newDoctorName}}</strong> {{$t('asMainDoctor')}}
                                                </v-list-tile-title>
                                            </v-list-tile>
                                        </template>
                                    </v-autocomplete>
                                    <v-text-field
                                        v-if="isNewDoctor"
                                        v-model="mainDoctorName"
                                        :label="$t('doctorName')"
                                    >
                                        <template v-slot:append>
                                            <v-btn icon @click="cancelNewDoctor">
                                                <v-icon>close</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>
                                    <v-checkbox
                                        v-model="noDoctor"
                                        color="primary"
                                        :label="$t('noMainDoctor')"
                                        class="mb-3 mt-1"
                                        :rules="doctorRules"
                                    >
                                    </v-checkbox>

                                    <v-autocomplete
                                        v-if="!noPhysio && !isNewPhysio"
                                        v-model="physioId"
                                        :search-input.sync="newPhysioName"
                                        :items="physiosList"
                                        :label="$t('physiotherapist')"
                                        item-text="name"
                                        item-value="id"
                                        :rules="physioRules"
                                    >
                                        <template v-slot:no-data>
                                            <v-list-tile @click="handleNewPhysio">
                                                <v-list-tile-title>
                                                    {{$t('add')}} <strong>{{newPhysioName}}</strong> {{$t('asPhysio')}}
                                                </v-list-tile-title>
                                            </v-list-tile>
                                        </template>
                                    </v-autocomplete>
                                    <v-text-field
                                        v-if="isNewPhysio"
                                        v-model="mainPhysioName"
                                        :label="$t('physiotherapist')"
                                    >
                                        <template v-slot:append>
                                            <v-btn icon @click="cancelNewPhysio">
                                                <v-icon>close</v-icon>
                                            </v-btn>
                                        </template>
                                    </v-text-field>

                                    <v-checkbox
                                        v-model="noPhysio"
                                        hide-details
                                        color="primary"
                                        :label="$t('noPhysiotherapist')"
                                        class="mb-4 mt-1"
                                        :rules="physioRules"
                                    >
                                    </v-checkbox>
                                    <v-layout v-for="(doctor, index) in otherDoctors" :key="index" row wrap>
                                        <v-flex md3 pr-2>
                                            <v-select
                                                :ref="'typeSelect_' + index"
                                                :value="doctor.pivot.type"
                                                @change="(value) => { setOtherDoctorType({index: index, data: value}) }"
                                                :items="[
                                                    {text: $t('cardiologist'), value: 'CARDIO'},
                                                    {text: $t('sportDoctor'), value: 'SPORT'},
                                                    {text: $t('rheumatologist'), value: 'RHUMAT'},
                                                    {text: $t('otherDoctor'), value: 'OTHER'},
                                                ]"
                                                :label="$t('type')"
                                                required
                                            ></v-select>
                                        </v-flex>
                                        <v-flex md4 v-if="doctor.pivot.type === 'OTHER'" pr-2>
                                            <v-text-field
                                                required
                                                :value="doctor.pivot.label"
                                                @change="(value) => { return setOtherDoctorLabel({index: index, data: value}) }"
                                                :label="$t('domain')"
                                            ></v-text-field>
                                        </v-flex>
                                        <v-flex md4>
                                            <v-autocomplete
                                                :value="doctor.id"
                                                @change="(value) => { return setOtherDoctorId({index: index, data: value}) }"
                                                :items="doctorsList"
                                                item-text="name"
                                                item-value="id"
                                                :label="$t('doctorName')"
                                                append-outer-icon="clear"
                                                @click:append-outer="removeDoctor(index)"
                                            ></v-autocomplete>
                                        </v-flex>

                                    </v-layout>
                                    <v-layout>
                                        <v-btn flat color="secondary" @click="addDoctor">{{$t('addDoctor')}}</v-btn>
                                    </v-layout>
                                </div>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="4">
                    <v-form ref="firmForm" v-model="firmInfoValid" lazy-validation>
                        <v-layout>
                            <v-flex md6 offset-md3>
                                <h3 class="headline mb-4">{{$t('howDidYouChooseThisCabinet')}}</h3>
                                <v-select
                                    :menu-props="{ maxHeight: '100%' }"
                                    v-model="selectFirm"
                                    :items="[
                                    {text: $t('viaDoctor'), value: 'doctor'},
                                    {text: $t('viaPhysio'), value: 'physio'},
                                    {text: $t('viaInternet'), value: 'internet'},
                                    {text: $t('viaReception'), value: 'reception'},
                                    {text: $t('viaEmergencies'), value: 'emergencies'},
                                    {text: $t('viaPersonAdvise'), value: 'person_advise'},
                                    {text: $t('other'), value: 'other'},
                                    {text: $t('dontKnow'), value: 'dont_know'}
                                    ]"
                                    :label="$t('via')"
                                    :rules="notEmpty"
                                ></v-select>
                                <v-text-field
                                    v-if="selectFirm === 'doctor' || selectFirm === 'physio' || selectFirm === 'other'"
                                    v-model="selectFirmOther"
                                    :label="$t('name')"
                                ></v-text-field>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="5">
                    <v-form ref="jobForm" v-model="jobInfoValid" lazy-validation>
                        <v-layout>
                            <v-flex md6 offset-md3>
                                <h3 class="headline mb-4">{{$t('whatsYourJob')}}</h3>
                                <v-select
                                    v-model="job"
                                    :items="[
                                    {text: $t('desk'), value: 'desk'},
                                    {text: $t('admin_manual'), value: 'admin_manual'},
                                    {text: $t('manual_light'), value: 'manual_light'},
                                    {text: $t('manual_heavy'), value: 'manual_heavy'},
                                    {text: $t('unemployed'), value: 'unemployed'},
                                    {text: $t('retired'), value: 'retired'},
                                    {text: $t('housewife'), value: 'housewife'},
                                    {text: $t('sport_pro'), value: 'sport_pro'},
                                    ]"
                                    :label="$t('job')"
                                    :rules="notEmpty"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="6">
                    <v-form ref="bmiForm" lazy-validation>
                        <v-container grid-list-md >
                            <v-layout>
                                <v-flex md4 offset-md3>
                                    <v-text-field v-model="height"
                                                  :rules="bmiFormHeightRules"
                                                  validate-on-blur
                                                  type="number"
                                                  :label="$t('whatIsYourHeight')"
                                                  suffix="cm"
                                                  required>
                                    </v-text-field>
                                    <v-text-field v-model="weight"
                                                  :rules="bmiFormHWeightRules"
                                                  validate-on-blur
                                                  type="number"
                                                  :label="$t('whatIsYourWeight')"
                                                  suffix="kg"
                                                  required>
                                        </v-text-field>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="7">
                    <v-form ref="smokingForm" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('doYouSmoke') }}</v-label>
                                    <v-radio-group v-model="isSmoker" row
                                                   :rules="smokingFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>
                                    <v-text-field
                                        v-if="isSmoker"
                                        :rules="smokingFormRules"
                                        validate-on-blur
                                        v-model="smoking_quantity"
                                        type="number"
                                        :label="$t('howManyCigarettesPerDay')"
                                        required>
                                    </v-text-field>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="8">
                    <v-form ref="diseasesForm" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md8 offset-md2>
                                    <v-label>{{ $t('doYouHaveAnyDisease') }}</v-label>
                                    <v-checkbox v-model="diabetes" :rules="diseasesRules" hide-details :label="$t('DISEASE_DIABETES')"></v-checkbox>
                                    <v-checkbox v-model="hypertension" :rules="diseasesRules" hide-details :label="$t('DISEASE_HYPERTENSION')"></v-checkbox>
                                    <v-checkbox v-model="cholesterol" :rules="diseasesRules" hide-details :label="$t('DISEASE_CHOLESTEROL')"></v-checkbox>
                                    <v-checkbox v-model="arthritis" :rules="diseasesRules" hide-details :label="$t('DISEASE_ARTHRITIS')"></v-checkbox>
                                    <v-checkbox v-model="fibrillation" :rules="diseasesRules" hide-details :label="$t('DISEASE_FIBRILLATION')"></v-checkbox>
                                    <v-checkbox v-model="coagulation_disorder" :rules="diseasesRules" hide-details :label="$t('DISEASE_COAGULATION_DISORDER')"></v-checkbox>
                                    <v-checkbox v-model="none" :rules="diseasesRules" :label="$t('DISEASE_NONE')"></v-checkbox>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-stepper-content>
                <v-stepper-content step="9">
                    <v-form ref="surgeryForm" v-model="surgeryFormValid" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('orthopedicOperation')}}</v-label>
                                    <v-radio-group v-model="previousSurgeries" row
                                                   :rules="surgeryFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                    <v-layout v-if="previousSurgeries" v-for="(surgery, index) in medical_histories" :key="'surgery-' + index">
                                        <v-flex md4>
                                            <v-text-field
                                                :ref="'surgery_' + index"
                                                :value="surgery.type"
                                                @change="(value) => { setPreviousSurgeryType({index: index, data: value}) }"
                                                :label="$t('type')"
                                                required>
                                            </v-text-field>
                                        </v-flex>
                                        <v-flex md5>
                                            <v-text-field
                                                :ref="'surgery_' + index"
                                                mask="####"
                                                :value="surgery.date"
                                                :label="$t('year')"
                                                append-icon="event"
                                                append-outer-icon="clear"
                                                @click:append-outer="removeMedicalHistory(index)"
                                                required
                                                :rules="notEmpty"
                                                @change="(value) => { setPreviousSurgeryDate({index: index, data: value}) }"
                                            >
                                            </v-text-field>
                                        </v-flex>
                                    </v-layout>
                                    <v-layout v-if="previousSurgeries" >
                                        <v-btn color="error" @click="addMedicalHistory"> {{ $t('addOperation') }}</v-btn>
                                    </v-layout>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>

                </v-stepper-content>
                <v-stepper-content step="10">
                    <v-form ref="painForm" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('doYouHavePain') }}</v-label>
                                    <v-radio-group v-model="pain" row
                                                   :rules="painFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                    <v-select v-if="pain"
                                              v-model="pain_since" :rules="painFormRules" validate-on-blur
                                              :items="[
                                                                {text: $t('SINCE_ACCIDENT'), value: 'SINCE_ACCIDENT'},
                                                                {text: $t('SINCE_LESS_THAN_A_MONTH'), value: 'SINCE_LESS_THAN_A_MONTH'},
                                                                {text: $t('SINCE_MORE_THAN_A_MONTH'), value: 'SINCE_MORE_THAN_A_MONTH'},
                                                                {text: $t('SINCE_SIX_MONTH'), value: 'SINCE_SIX_MONTH'},
                                                                {text: $t('SINCE_ONE_YEAR'), value: 'SINCE_ONE_YEAR'},
                                                                {text: $t('SINCE_TWO_YEARS'), value: 'SINCE_TWO_YEARS'},
                                                                {text: $t('SINCE_FIVE_YEARS'), value: 'SINCE_FIVE_YEARS'},
                                                                {text: $t('SINCE_TEN_YEARS'), value: 'SINCE_TEN_YEARS'}
                                                        ]"
                                              :label="$t('howLong')"
                                              required
                                    ></v-select>

                                    <v-menu
                                        v-if="pain && pain_since === 'SINCE_ACCIDENT'"
                                        v-model="showPainAccidentDatePicker"
                                        :rules="painFormRules"
                                        validate-on-blur
                                        :close-on-content-click="false"
                                        lazy
                                        transition="scale-transition"
                                        offset-y
                                        full-width
                                        max-width="290px"
                                        min-width="290px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="computedAccidentDateFormatted"
                                                :label="$t('accidentDate')"
                                                append-icon="event"
                                                @blur="accident_date = parseDate(computedAccidentDateFormatted)"
                                                v-on="on"
                                                :rules="notEmpty"
                                                required
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker v-model="accident_date" no-title
                                                       :locale="locale"
                                                       @input="showPainAccidentDatePicker = false"></v-date-picker>
                                    </v-menu>
                                    <v-label  v-if="pain">{{ $t('effortPain') }}</v-label>
                                    <v-radio-group v-model="pain_effort" row
                                                   :rules="painFormRules"
                                                   validate-on-blur
                                                   v-if="pain">
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>
                                    <v-label  v-if="pain">{{ $t('restPain') }}</v-label>
                                    <v-radio-group v-model="pain_rest" row
                                                   :rules="painFormRules"
                                                   validate-on-blur
                                                   v-if="pain">
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>
                                    <v-label  v-if="pain">{{ $t('nightPain') }}</v-label>
                                    <v-radio-group v-model="pain_night" row
                                                   :rules="painFormRules"
                                                   validate-on-blur
                                                   v-if="pain">
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>

                </v-stepper-content>
                <v-stepper-content step="11">
                    <v-form ref="blockingForm" lazy-validation>
                        <v-container grid-list-md >
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('haveBlockage') }}</v-label>
                                    <v-radio-group v-model="blocking" row
                                                   :rules="blockingFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                    <v-select v-if="blocking"
                                              v-model="blocking_since"
                                              :rules="blockingFormRules"
                                              :items="[
                                                                {text: $t('SINCE_ACCIDENT'), value: 'SINCE_ACCIDENT'},
                                                                {text: $t('SINCE_LESS_THAN_A_MONTH'), value: 'SINCE_LESS_THAN_A_MONTH'},
                                                                {text: $t('SINCE_MORE_THAN_A_MONTH'), value: 'SINCE_MORE_THAN_A_MONTH'},
                                                                {text: $t('SINCE_SIX_MONTH'), value: 'SINCE_SIX_MONTH'},
                                                                {text: $t('SINCE_ONE_YEAR'), value: 'SINCE_ONE_YEAR'},
                                                                {text: $t('SINCE_TWO_YEARS'), value: 'SINCE_TWO_YEARS'},
                                                                {text: $t('SINCE_FIVE_YEARS'), value: 'SINCE_FIVE_YEARS'},
                                                                {text: $t('SINCE_TEN_YEARS'), value: 'SINCE_TEN_YEARS'}
                                                        ]"
                                              :label=" $t('howLong')"
                                              required
                                    ></v-select>

                                    <v-menu
                                        v-if="blocking && blocking_since === 'SINCE_ACCIDENT'"
                                        v-model="showBlockingAccidentDatePicker"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        lazy
                                        offset-y
                                        full-width
                                        max-width="290px"
                                        min-width="290px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="computedAccidentDateFormatted"
                                                :label="$t('accidentDate')"
                                                append-icon="event"
                                                @blur="accident_date = parseDate(computedAccidentDateFormatted)"
                                                v-on="on"
                                                :rules="notEmpty"
                                                required
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker v-model="accident_date" no-title
                                                       :locale="locale"
                                                       @input="showBlockingAccidentDatePicker = false"></v-date-picker>
                                    </v-menu>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>

                </v-stepper-content>
                <v-stepper-content step="12">
                    <v-form ref="instabilityForm" lazy-validation>
                        <v-container grid-list-md >
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('haveInstability') }}</v-label>
                                    <v-radio-group v-model="instability" row
                                                   :rules="instabilityFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                    <v-select v-if="instability"
                                              v-model="instability_since"
                                              :rules="instabilityFormRules"
                                              validate-on-blur
                                              :items="[
                                                                {text: $t('SINCE_ACCIDENT'), value: 'SINCE_ACCIDENT'},
                                                                {text: $t('SINCE_LESS_THAN_A_MONTH'), value: 'SINCE_LESS_THAN_A_MONTH'},
                                                                {text: $t('SINCE_MORE_THAN_A_MONTH'), value: 'SINCE_MORE_THAN_A_MONTH'},
                                                                {text: $t('SINCE_SIX_MONTH'), value: 'SINCE_SIX_MONTH'},
                                                                {text: $t('SINCE_ONE_YEAR'), value: 'SINCE_ONE_YEAR'},
                                                                {text: $t('SINCE_TWO_YEARS'), value: 'SINCE_TWO_YEARS'},
                                                                {text: $t('SINCE_FIVE_YEARS'), value: 'SINCE_FIVE_YEARS'},
                                                                {text: $t('SINCE_TEN_YEARS'), value: 'SINCE_TEN_YEARS'}
                                                        ]"
                                              :label="$t('howLong')"
                                              required
                                    ></v-select>

                                    <v-menu
                                        v-if="instability && instability_since === 'SINCE_ACCIDENT'"
                                        v-model="showInstabilityAccidentDatePicker"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        lazy
                                        offset-y
                                        full-width
                                        max-width="290px"
                                        min-width="290px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="computedAccidentDateFormatted"
                                                :label="$t('accidentDate')"
                                                append-icon="event"
                                                @blur="accident_date = parseDate(computedAccidentDateFormatted)"
                                                v-on="on"
                                                :rules="notEmpty"
                                                required
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker v-model="accident_date" no-title
                                                       :locale="locale"
                                                       @input="showInstabilityAccidentDatePicker = false"></v-date-picker>
                                    </v-menu>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>

                </v-stepper-content>
                <v-stepper-content step="13">
                    <v-form ref="inflatedForm" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ $t('haveSwelling') }}</v-label>
                                    <v-radio-group v-model="inflated" row
                                                   :rules="inflatedFormRules"
                                                   validate-on-blur>
                                        <v-radio :label="$t('yes')" :value="true"></v-radio>
                                        <v-radio :label="$t('no')" :value="false"></v-radio>
                                    </v-radio-group>

                                    <v-select v-if="inflated"
                                              v-model="inflated_since"
                                              :rules="inflatedFormRules"
                                              validate-on-blur
                                              :items="[
                                                                {text: $t('SINCE_ACCIDENT'), value: 'SINCE_ACCIDENT'},
                                                                {text: $t('SINCE_LESS_THAN_A_MONTH'), value: 'SINCE_LESS_THAN_A_MONTH'},
                                                                {text: $t('SINCE_MORE_THAN_A_MONTH'), value: 'SINCE_MORE_THAN_A_MONTH'},
                                                                {text: $t('SINCE_SIX_MONTH'), value: 'SINCE_SIX_MONTH'},
                                                                {text: $t('SINCE_ONE_YEAR'), value: 'SINCE_ONE_YEAR'},
                                                                {text: $t('SINCE_TWO_YEARS'), value: 'SINCE_TWO_YEARS'},
                                                                {text: $t('SINCE_FIVE_YEARS'), value: 'SINCE_FIVE_YEARS'},
                                                                {text: $t('SINCE_TEN_YEARS'), value: 'SINCE_TEN_YEARS'}
                                                        ]"
                                                :label="$t('howLong')"
                                              required
                                    ></v-select>

                                    <v-menu
                                        v-if="inflated && inflated_since === 'SINCE_ACCIDENT'"
                                        v-model="showInflatedAccidentDatePicker"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        lazy
                                        offset-y
                                        full-width
                                        max-width="290px"
                                        min-width="290px">
                                        <template v-slot:activator="{ on }">
                                            <v-text-field
                                                v-model="computedAccidentDateFormatted"
                                                :label="$t('accidentDate')"
                                                append-icon="event"
                                                @blur="accident_date = parseDate(computedAccidentDateFormatted)"
                                                v-on="on"
                                                :rules="notEmpty"
                                                required
                                            ></v-text-field>
                                        </template>
                                        <v-date-picker v-model="accident_date" no-title
                                                       :locale="locale"
                                                       @input="showInflatedAccidentDatePicker = false"></v-date-picker>
                                    </v-menu>

                                    <v-select v-if="inflated"
                                              v-model="inflated_count"
                                              :items="[
                                                                {text: $t('oneTime'), value: 1},
                                                                {text: $t('severalTimes'), value: 2}
                                                        ]"
                                                :label="$t('howManyTimes')"
                                              required
                                    ></v-select>

                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>

                </v-stepper-content>
                <v-stepper-content step="14">
                    <v-form ref="tegnerForm" v-model="tegnerInfoValid" lazy-validation>
                        <v-container grid-list-md>
                            <v-layout>
                                <v-flex md6 offset-md3>
                                    <v-label>{{ tegnerScore.score_question_text }}</v-label>
                                    <div class="custom-radio">
                                        <div class="radio mb-2"
                                             v-for="question_tegner in tegnerScore.score_question_answer"
                                             :key="question_tegner.id">
                                            <input type="radio" name="tegner" :value="question_tegner.value"
                                                   v-model="tegner"
                                                   :id="'answer'+question_tegner.id">
                                            <label class="v-label theme--light"
                                                   :for="'answer'+question_tegner.id">{{ question_tegner.text }}<span
                                                v-if="question_tegner.remarks"
                                                v-html="'<br>' + question_tegner.remarks"></span></label>
                                        </div>
                                    </div>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-form>
                </v-stepper-content>
            </v-stepper-items>
        </v-stepper>

        <v-card-actions class="absolute-bottom-right">
            <v-btn v-if="step > 1" @click="(step > 1) ? step = step - 1 : null" class="left">
                {{ $t('back') }}
            </v-btn>
            <v-btn color="success" @click="validate" class="right"> {{ $t('next') }}</v-btn>
        </v-card-actions>

    </v-container>

</template>

<script>
    import { mapActions, mapState, mapMutations } from 'vuex'
    export default {
        name: "PatientMedicalCaseForm",
        props: {
            listener: Function
        },
        data: () => ({
            showPainAccidentDatePicker: false,
            showInflatedAccidentDatePicker: false,
            showInstabilityAccidentDatePicker: false,
            showBlockingAccidentDatePicker: false,
            surgeryFormValid: false,
            tegnerInfoValid: false,
            patientInfoValid: false,
            doctorsInfoValid: false,
            firmInfoValid: false,
            jobInfoValid: false,
            step: 1,
            finalStep: 15,
            isSmoker: null,
            previousSurgeries: false,
            pain: false,
            blocking: false,
            instability: false,
            inflated: false,
            none: null,
            computedAccidentDateFormatted: '',
            birthDateFormatted: '',
            acceptCGU: true,
            newDoctorName: null,
            isNewDoctor: false,
            newPhysioName: null,
            isNewPhysio: false,
        }),
        created() {
            this.pain = this.pain_since == null ? null : true
            this.isSmoker = this.smoking_quantity == null ? null : (this.smoking_quantity > 0)
            this.blocking = this.blocking_since == null ? (this.$store.state.medical_cases.form.id ? false : null) : true
            this.previousSurgeries = this.medical_histories.length === 0 ? (this.$store.state.medical_cases.form.id ? false : null) : true
            this.instability = this.instability_since == null ? (this.$store.state.medical_cases.form.id ? false : null) : true
            this.inflated = this.inflated_since == null ? (this.$store.state.medical_cases.form.id ? false : null) : true
            this.computedAccidentDateFormatted = this.accident_date == null ? null : this.formatDate(this.accident_date)
        },
        mounted() {
            this.getTegner()
            this.getAllDoctors()
            this.getAllPhysios()
        },
        computed: {
            locale() {
                return this.$i18n.locale
            },
            countryList() {
                const countries = this.$t('countries')
                return Object.keys(countries).map(key => {
                    return {
                        name: countries[key],
                        code: key
                    }
                })
            },
            ...mapState('doctors', {
                doctorsList: state => [{id: null, name: ''}, ...state.all]
            }),
            ...mapState('physios', {
                physiosList: state => [{id: null, name: ''}, ...state.all]
            }),

            ...mapState('patients', {
                selectedPatient: state => state.selectedPatient,
                tegnerScore: state => state.tegnerScore,
            }),
            notEmpty(){
                return [
                    v => !!v || this.$t('pleaseSelectValue')
                ]
            },
            addressRules() {
                return [
                    v => !!v || this.$t('addressRequired')
                ]
            },
            postcodeRules() {
                return [
                    v => !!v || this.$t('postcodeRequired')
                ]
            },
            cityRules() {
                return [
                    v => !!v || this.$t('cityRequired')
                ]
            },
            countryRules() {
                return [
                    v => !!v || this.$t('countryRequired')
                ]
            },
            cguRules() {
                return [
                    v => !!v || this.$t('cguRequired')
                ]
            },
            doctorRules() {
                return [
                    (this.step !== 3 || (this.mainDoctorId != null) || this.noDoctor || this.isNewDoctor) || this.$t('pleaseSelectValue')
                ];
            },
            physioRules() {
                return [
                    (this.step !== 3 || (this.physioId != null) || this.noPhysio || this.isNewPhysio) || this.$t('pleaseSelectValue')
                ];
            },
            bmiFormHeightRules() {
                return [
                    (this.step !== 6 || (this.height != null)) || this.$t('pleaseSelectValue')
                ];
            },
            bmiFormHWeightRules() {
                return [
                    (this.step !== 6 || (this.weight != null)) || this.$t('pleaseSelectValue')
                ];
            },
            smokingFormRules() {
                return [
                    (this.step !== 7 || (this.isSmoker === false) || (this.isSmoker && this.smoking_quantity !== null))
                    || this.$t('pleaseSelectValue')
                ];
            },
            diseasesRules() {
                return [
                    (this.step !== 8 || (this.diabetes
                     || this.hypertension
                     || this.cholesterol
                     || this.arthritis
                     || this.fibrillation
                     || this.coagulation_disorder
                     || this.none)) || this.$t('pleaseSelectValue')
                ];
            },
            surgeryFormRules() {
                return [
                    (this.step !== 9|| (this.previousSurgeries != null))
                    || this.$t('pleaseSelectValue')
                ];
            },
            painFormRules() {
                return [
                    (this.step !== 10 || (
                        this.pain === false ||
                        (this.pain_since !== null &&
                        this.pain_effort !== null &&
                        this.pain_rest !== null &&
                        this.pain_night !== null)
                    ))
                    || this.$t('pleaseSelectValue')
                ];
            },
            blockingFormRules() {
                return [
                    (this.step !== 11 || (
                        this.blocking === false ||
                        (this.blocking_since !== null)
                    ))
                    || this.$t('pleaseSelectValue')
                ];
            },
            instabilityFormRules() {
                return [
                    (this.step !== 12 || (
                        this.instability === false ||
                        (this.instability_since !== null)
                    ))
                    || this.$t('pleaseSelectValue')
                ];
            },
            inflatedFormRules() {
                return [
                    (this.step !== 13 || (
                        this.inflated === false ||
                        (this.inflated_since !== null)
                    ))
                    || this.$t('pleaseSelectValue')
                ];
            },
            address: {
                get() {
                    return this.$store.state.patients.contactForm.address
                },
                set(value) {
                    this.setAddress(value)
                }
            },
            postcode: {
                get() {
                    return this.$store.state.patients.contactForm.postcode
                },
                set(value) {
                    this.setPostcode(value)
                }
            },
            city: {
                get() {
                    return this.$store.state.patients.contactForm.city
                },
                set(value) {
                    this.setCity(value)
                }
            },
            country: {
                get() {
                    return this.$store.state.patients.contactForm.country
                },
                set(value) {
                    this.setCountry(value)
                }
            },
            declineDataCollection: {
                get() {
                    return this.$store.state.patients.form.decline_data_collection
                },
                set(value) {
                    this.setDeclineDataCollection(value)
                }
            },
            mainDoctorId: {
                get() {
                    return this.$store.state.patients.doctorsForm.main.id
                },
                set(value) {
                    this.setMainDoctorId(value)
                }
            },
            mainDoctorName: {
                get() {
                    return this.$store.state.patients.doctorsForm.mainName
                },
                set(value) {
                    this.setMainDoctorName(value)
                }
            },
            mainPhysioName: {
                get() {
                    return this.$store.state.patients.doctorsForm.physioName
                },
                set(value) {
                    this.setMainPhysioName(value)
                }
            },
            noDoctor: {
                get() {
                    return this.$store.state.patients.doctorsForm.no_doctor
                },
                set(value) {
                    this.setNoDoctor(value)
                }
            },
            physioId: {
                get() {
                    return this.$store.state.patients.doctorsForm.physio.id
                },
                set(value) {
                    this.setPhysioId(value)
                }
            },
            noPhysio: {
                get() {
                    return this.$store.state.patients.doctorsForm.no_physio
                },
                set(value) {
                    this.setNoPhysio(value)
                }
            },
            otherDoctors: {
                get() {
                    return this.$store.state.patients.doctorsForm.others
                },
                set(value) {
                    this.setOtherDoctors(value)
                }
            },
            selectFirm: {
                get() {
                    return this.$store.state.patients.firmForm.firm_select
                },
                set(value) {
                    this.setSelectFirm(value)
                }
            },
            selectFirmOther: {
                get() {
                    return this.$store.state.patients.firmForm.firm_select_other
                },
                set(value) {
                    this.setSelectFirmOther(value)
                }
            },
            job: {
                get() {
                    return this.$store.state.patients.jobForm.job
                },
                set(value) {
                    this.setJob(value)
                }
            },
            tegner: {
                get() {
                    return this.$store.state.patients.tegnerForm.tegner
                },
                set(value) {
                    this.setTegner(value)
                }
            },
            height: {
                get() {
                    return this.$store.state.medical_cases.form.height
                },
                set(value) {
                    this.setHeight(value)
                }
            },
            weight: {
                get() {
                    return this.$store.state.medical_cases.form.weight
                },
                set(value) {
                    this.setWeight(value)
                }
            },
            smoking_quantity: {
                get() {
                    return this.$store.state.medical_cases.form.smoking_quantity
                },
                set(value) {
                    this.setSmokingQuantity(value)
                }
            },
            diabetes: {
                get() {
                    return this.$store.state.medical_cases.form.diabetes
                },
                set(value) {
                    this.setDiabetes(value)
                }
            },
            hypertension: {
                get() {
                    return this.$store.state.medical_cases.form.hypertension
                },
                set(value) {
                    this.setHypertension(value)
                }
            },
            cholesterol: {
                get() {
                    return this.$store.state.medical_cases.form.cholesterol
                },
                set(value) {
                    this.setCholesterol(value)
                }
            },
            arthritis: {
                get() {
                    return this.$store.state.medical_cases.form.arthritis
                },
                set(value) {
                    this.setArthritis(value)
                }
            },
            fibrillation: {
                get() {
                    return this.$store.state.medical_cases.form.fibrillation
                },
                set(value) {
                    this.setFibrillation(value)
                }
            },
            coagulation_disorder: {
                get() {
                    return this.$store.state.medical_cases.form.coagulation_disorder
                },
                set(value) {
                    this.setCoagulationDisorder(value)
                }
            },
            pain_since: {
                get() {
                    return this.$store.state.medical_cases.form.pain_since
                },
                set(value) {
                    this.setPainSince(value)
                }
            },
            pain_effort: {
                get() {
                    return this.$store.state.medical_cases.form.pain_effort
                },
                set(value) {
                    this.setPainEffort(value)
                }
            },
            pain_rest: {
                get() {
                    return this.$store.state.medical_cases.form.pain_rest
                },
                set(value) {
                    this.setPainRest(value)
                }
            },
            pain_night: {
                get() {
                    return this.$store.state.medical_cases.form.pain_night
                },
                set(value) {
                    this.setPainNight(value)
                }
            },
            accident_date: {
                get() {
                    return this.$store.state.medical_cases.form.accident_date
                },
                set(value) {
                    this.setAccidentDate(value)
                }
            },
            blocking_since: {
                get() {
                    return this.$store.state.medical_cases.form.blocking_since
                },
                set(value) {
                    this.setBlockingSince(value)
                }
            },
            instability_since: {
                get() {
                    return this.$store.state.medical_cases.form.instability_since
                },
                set(value) {
                    this.setInstabilitySince(value)
                }
            },
            inflated_since: {
                get() {
                    return this.$store.state.medical_cases.form.inflated_since
                },
                set(value) {
                    this.setInflatedSince(value)
                }
            },
            inflated_count: {
                get() {
                    return this.$store.state.medical_cases.form.inflated_count
                },
                set(value) {
                    this.setInflatedCount(value)
                }
            },
            medical_histories: {
                get() {
                    return this.$store.state.medical_cases.form.medical_histories
                }
            }
        },
        methods: {
            handleNewDoctor(){
                this.setMainDoctorName(this.newDoctorName)
                this.newDoctorName = null
                this.isNewDoctor = true
            },

            handleNewPhysio(){
                this.setPhysioName(this.newPhysioName)
                this.newPhysioName = null
                this.isNewPhysio = true
            },
            cancelNewDoctor(){
                this.setMainDoctorName(null)
                this.newDoctorName = null
                this.isNewDoctor = false
            },
            cancelNewPhysio(){
                this.setMainPhysioName(null)
                this.newPhysioName = null
                this.isNewPhysio = false
            },
            back() {
                this.$router.back()
            },
            validate () {
                switch (this.step)
                {
                    case 1:
                        if(this.$refs.patientForm.validate())
                            this.submitPatientAddress()
                        break
                    case 2:
                        this.submitStudy()
                        break
                    case 3:
                         if(this.$refs.doctorForm.validate())
                            this.submitDoctors()
                         break
                    case 4:
                        if(this.$refs.firmForm.validate())
                            this.submitFirm()
                        break
                    case 5:
                        if(this.$refs.jobForm.validate())
                            this.submitJob()
                        break
                    case 6:
                        if(this.$refs.bmiForm.validate())
                            this.submit()
                        break
                    case 7:
                        if(this.$refs.smokingForm.validate())
                            this.submit()
                        break
                    case 8:
                        if(this.$refs.diseasesForm.validate())
                            this.submit()
                        break
                    case 9:
                        if(this.$refs.surgeryForm.validate())
                            this.submit()
                        break
                    case 10:
                        if(this.$refs.painForm.validate())
                            this.submit()
                        break
                    case 11:
                        if(this.$refs.blockingForm.validate())
                            this.submit()
                        break
                    case 12:
                        if(this.$refs.instabilityForm.validate())
                            this.submit()
                        break
                    case 13:
                        if(this.$refs.inflatedForm.validate())
                            this.submit()
                        break
                    case 14:
                        if(this.$refs.tegnerForm.validate())
                            this.submit()
                        break
                    default:
                        break
                }

            },
            submit() {
                if (!this.isSmoker) {
                    this.setSmokingQuantity(0)
                }

                let methodForSubmit = this.step < 14 ?
                    this.updateMedicalFormFromNotification : this.updateTegnerInfosFromNotification;


                methodForSubmit().then(response => {
                    //Move back to the user list
                    //this.$refs.form.reset()
                    this.step = this.step + 1
                    this.$refs.diseasesForm.resetValidation()

                    if (this.step >= this.finalStep) {
                        window.removeEventListener('beforeunload', this.listener);
                        this.$router.go(0)
                    }
                }).catch(reason => {
                    //Stay here..
                    console.log(reason)
                })
            },
            submitPatientAddress() {
                this.updateAddressInfosFromNotification().then(response => {
                    this.patientInfoValid = true
                    this.step = this.step + 1
                }).catch(reason => {
                    console.log(reason)
                })
            },
            submitStudy(){
                this.updateStudyInfosFromNotification().then(response => {
                    this.step = this.step + 1
                }).catch(reason => {
                    console.log(reason)
                })
            },
            submitDoctors(){
                this.updateDoctorInfosFromNotification().then(response => {
                    this.step = this.step + 1
                }).catch(reason => {
                    console.log(reason)
                })
            },
            submitFirm(){
                this.updateFirmInfosFromNotification().then(response => {
                    this.step = this.step + 1
                }).catch(reason => {
                    console.log(reason)
                })
            },
            submitJob(){
                this.updateJobInfosFromNotification().then(response => {
                    this.step = this.step + 1
                }).catch(reason => {
                    console.log(reason)
                })
            },
            parseDate (date) {
                if (!date) return null

                console.log("from comp: " + date)
                let day;
                let month;
                let year;

                if (this.$i18n.locale === 'fr') {
                    [day, month, year] = date.split('/')
                }
                else {
                    [month, day, year] = date.split('/')
                }

                return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
            },
            formatDate(date) {
                if (!date) return null

                const [year, month, day] = date.split('-')

                if (this.$i18n.locale === 'fr') {
                    return `${day}/${month}/${year}`
                }
                else {
                    return `${month}/${day}/${year}`
                }

            },
            ...mapMutations('medical_cases', {
                setHeight: 'setFormHeight',
                setWeight: 'setFormWeight',
                setSmokingQuantity: 'setFormSmokingQuantity',
                setSelectedMedicalCaseId: 'setSelectedMedicalCaseId',
                setDiabetes: 'setFormDiabetes',
                setHypertension: 'setFormHypertension',
                setCholesterol: 'setFormCholesterol',
                setArthritis: 'setFormArthritis',
                setFibrillation: 'setFormFibrillation',
                setCoagulationDisorder: 'setFormCoagulationDisorder',
                setPreviousSurgeryType: 'setPreviousSurgeryType',
                setPreviousSurgeryDate: 'setPreviousSurgeryDate',
                removeMedicalHistory: 'removeMedicalHistory',
                addMedicalHistory: 'addMedicalHistory',
                setPainSince: 'setFormPainSince',
                setPainEffort: 'setFormPainEffort',
                setPainRest: 'setFormPainRest',
                setPainNight: 'setFormPainNight',
                setAccidentDate: 'setFormAccidentDate',
                setBlockingSince: 'setFormBlockingSince',
                setInstabilitySince: 'setFormInstabilitySince',
                setInflatedSince: 'setFormInflatedSince',
                setInflatedCount: 'setFormInflatedCount',
            }),
            ...mapMutations('patients', {
                setTegner: 'setFormTegner',
                setDeclineDataCollection: 'setFormDeclineDataCollection',
                setAddress: 'setFormAddress',
                setPostcode: 'setFormPostcode',
                setCity: 'setFormCity',
                setCountry: 'setFormCountry',
                setNotification: 'setFormNotification',
                setMainDoctorId: 'setFormMainDoctorId',
                setMainDoctorName: 'setFormMainDoctorName',
                setPhysioName: 'setFormPhysioName',
                setNoDoctor: 'setFormNoDoctor',
                setPhysioId: 'setFormPhysioId',
                setNoPhysio: 'setFormNoPhysio',
                setSelectedPatientId: 'setSelectedPatientId',
                initPatientForms: 'initPatientForms',
                setOtherDoctorType: 'setFormOtherDoctorType',
                setOtherDoctorId: 'setFormOtherDoctorId',
                setOtherDoctorLabel: 'setFormOtherDoctorLabel',
                addDoctor: 'addNewEmptyDoctor',
                setSelectFirm: 'setFormSelectFirm',
                setSelectFirmOther: 'setFormSelectFirmOther',
                setOtherDoctors: 'setOtherDoctors',
                setJob: 'setFormJob',
                removeDoctor: 'removeDoctorAtIndex',
            }),
            ...mapActions('patients', ['getTegner', 'updateTegnerInfosFromNotification']),
            ...mapActions('medical_cases', ['updateMedicalFormFromNotification']),
            ...mapActions('doctors', ['getAllDoctors']),
            ...mapActions('physios', ['getAllPhysios']),
            ...mapActions('patients', [
                'updateAddressInfosFromNotification',
                'updateStudyInfosFromNotification',
                'updateDoctorInfosFromNotification',
                'updateFirmInfosFromNotification',
                'updateJobInfosFromNotification',
            ]),
        },
        watch: {
            accident_date(date) {
                this.computedAccidentDateFormatted = date ? this.$options.filters.formatedDate(date) : ''
            },
            locale() {
                this.getTegner()
            }
        },
    }
</script>

<style scoped>
    .border-red {
        border-color: red;
        border-width: 2px;
    }
    .h-100 {
        height: 100%;
    }
</style>
